import { useEffect, useMemo, useRef, useState } from "react"
import Address from "components/Address"
import AmbientZoomImage from "components/AmbientZoomImage"
import DotsLoader from "components/DotsLoader"
import MetadataAwareTokenView from "components/MetadataAwareTokenView"
import useRandomHeroArtwork, { RandomHeroArtwork } from "hooks/useRandomHeroArtwork"
import useWindowSize from "hooks/useWindowSize"
import { getArtworkFramePadding } from "utils/galleryLayout"

const getHeroArtworkFallbackWidth = (windowWidth: number) => {
  const framePadding = getArtworkFramePadding(windowWidth)

  if (windowWidth >= 1280) return 648
  if (windowWidth >= 768) return Math.floor(windowWidth * 0.72) - framePadding
  return Math.max(240, windowWidth - 40 - framePadding)
}

const getHeroArtworkMaxHeight = (windowHeight: number, windowWidth: number) => {
  if (windowWidth < 768) return 460
  if (windowHeight <= 0) return 640
  return Math.max(440, Math.min(680, windowHeight - 280))
}

const getContainedArtworkSize = (width: number, maxHeight: number, aspectRatio: number) => {
  const safeAspectRatio = aspectRatio > 0 ? aspectRatio : 1
  let artWidth = width
  let artHeight = artWidth / safeAspectRatio

  if (artHeight > maxHeight) {
    artHeight = maxHeight
    artWidth = artHeight * safeAspectRatio
  }

  return { width: artWidth, height: artHeight }
}

const HeroArtworkCaption = ({ artwork, width }: { artwork: RandomHeroArtwork; width: number }) => {
  const external = artwork.href.startsWith("http")

  return (
    <figcaption
      className="mt-3 max-w-full gallery-label text-gallery-muted text-right leading-relaxed"
      style={{ width }}
    >
      <a
        href={artwork.href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className="gallery-link text-gallery-text"
      >
        {artwork.label}
      </a>
      {artwork.year && <span>, {artwork.year}</span>}
      {artwork.ownerAddress && (
        <>
          <br />
          <span>owned by </span>
          <Address address={artwork.ownerAddress} />
        </>
      )}
    </figcaption>
  )
}

const OneOfOneArtwork = ({
  artwork,
  width,
  maxHeight,
  zoomEnabled,
}: {
  artwork: RandomHeroArtwork
  width: number
  maxHeight: number
  zoomEnabled: boolean
}) => {
  const { width: artWidth, height: artHeight } = getContainedArtworkSize(width, maxHeight, artwork.aspectRatio)

  if (!artwork.mediaUrl) {
    return (
      <div className="flex items-center justify-center bg-gallery-subtle" style={{ width: artWidth, height: artHeight }}>
        <span className="gallery-label text-gallery-muted">Artwork unavailable</span>
      </div>
    )
  }

  if (artwork.mediaType === "video") {
    return (
      <div className="flex items-center justify-center" style={{ width: artWidth, height: artHeight }}>
        <video src={artwork.mediaUrl} autoPlay muted loop playsInline className="h-full w-full object-contain" />
      </div>
    )
  }

  return (
    <AmbientZoomImage
      src={artwork.mediaUrl}
      alt={artwork.title}
      width={artWidth}
      height={artHeight}
      enabled={zoomEnabled}
      eager
    />
  )
}

const HomeHeroArtwork = () => {
  const figureRef = useRef<HTMLElement>(null)
  const [measuredWidth, setMeasuredWidth] = useState(0)
  const windowSize = useWindowSize()
  const { artwork, loading, error } = useRandomHeroArtwork()
  const framePadding = getArtworkFramePadding(windowSize.width)
  const width = Math.max(
    180,
    Math.floor((measuredWidth || getHeroArtworkFallbackWidth(windowSize.width) + framePadding) - framePadding)
  )
  const maxHeight = getHeroArtworkMaxHeight(windowSize.height, windowSize.width)
  const artworkSize = artwork ? getContainedArtworkSize(width, maxHeight, artwork.aspectRatio) : undefined
  const external = artwork?.href.startsWith("http") || false
  // The home hero is a navigation link; only enable the loupe on hover-capable
  // devices so a tap still opens the piece on touch.
  const canHover = useMemo(
    () => typeof window !== "undefined" && Boolean(window.matchMedia?.("(hover: hover) and (pointer: fine)").matches),
    []
  )

  useEffect(() => {
    const element = figureRef.current
    if (!element) return

    const measure = () => {
      setMeasuredWidth(element.getBoundingClientRect().width)
    }

    measure()

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure)
      return () => window.removeEventListener("resize", measure)
    }

    const observer = new ResizeObserver(measure)
    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  return (
    <figure ref={figureRef} className="m-0">
      {artwork && artworkSize ? (
        <div className="inline-flex max-w-full flex-col items-center">
          <a
            href={artwork.href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            className="gallery-hero-art-link"
            aria-label={`Open ${artwork.label}`}
          >
            <div className="gallery-art-frame">
              {artwork.kind === "series" && artwork.tokenId && artwork.contractAddress ? (
                <MetadataAwareTokenView
                  project={artwork.featuredProject}
                  contractAddress={artwork.featuredProject ? undefined : artwork.contractAddress}
                  chainId={artwork.featuredProject ? undefined : artwork.chainId}
                  tokenId={artwork.tokenId}
                  width={width}
                  fallbackAspectRatio={artwork.aspectRatio}
                  maxHeight={maxHeight}
                  loupe={canHover}
                  loupeZoom={3}
                />
              ) : artwork.kind === "one-of-one" ? (
                <OneOfOneArtwork artwork={artwork} width={width} maxHeight={maxHeight} zoomEnabled={canHover} />
              ) : null}
            </div>
          </a>
          <HeroArtworkCaption artwork={artwork} width={artworkSize.width} />
        </div>
      ) : (
        <div className="gallery-art-frame">
          <div
            className="flex items-center justify-center bg-gallery-subtle"
            style={{ width, height: Math.min(maxHeight, width) }}
          >
            {error && !loading ? (
              <span className="gallery-label text-gallery-muted">Artwork unavailable</span>
            ) : (
              <DotsLoader label="Loading artwork" shuffleKey="home-hero" />
            )}
          </div>
        </div>
      )}
    </figure>
  )
}

export default HomeHeroArtwork
